import React, { useState, useEffect, useRef, useReducer, useMemo } from 'react'
import ReactModal from 'react-modal'
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg'
import { useTranslation } from 'react-i18next'
import '../../styles/modals/StudentInfoModal.css'
import '../../styles/modals/StudentModal.css'
import i18n from 'i18next'
import { useForm } from 'react-hook-form'
import { ReactComponent as Save } from '../../assets/svg/Save.svg'
import { Toaster } from 'react-hot-toast'
import TextField from '@mui/material/TextField'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { formatDateOnly, format_day, notify } from '../../utils/util'
import { isQRCodeLoginEnabled } from '../../utils/qrCodeLogin'
import { deleteStudentAsync, email_exists, generate_new_password, generateLoginQRCodeForStudent, updateStudent } from '../../server/server'
import { useUser } from '../../auth'
import { localeMap } from '../../constants/Constants'
import TransferToAnotherClassModal from './TransferToAnotherClassModal'
import SelectInput from '../SelectInput'
import { useAppContext } from '../../context/AppContext'
import { env } from '../../constants/Connections'
import ConfirmationModal from './ConfirmationModal'
import { format, isToday } from 'date-fns'
import Loading from '../Loading'
import { QRCodeCanvas } from 'qrcode.react'
import { useInstitution } from '../../auth/InstitutionContext'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'

function StudentInfoModal({ studentInfoOpen, closeStudentInfo, student, currentClass, updateClassStudents, reloadStudentsList }) {
    const { t, locale } = useTranslation()
    const { appState } = useAppContext()
    const { institution, setInstitution } = useInstitution()
    useTrackedSurface(ANALYTICS_EVENTS.MANAGEMENT_STUDENT_VIEWED, {
        active: studentInfoOpen,
        contentId: student?.id,
        content: `${student?.first_name || ''} ${student?.last_name || ''}`.trim(),
    })
    const additionalFields = appState?.additionalFields?.fields ?? []
    const [loadingQrCode, setLoadingQrCode] = useState(true)
    const { register, handleSubmit, watch, formState, reset, setValue, getValues, setError } = useForm()
    const { isDirty, isValid, errors } = formState
    const { user } = useUser()
    const [additionalFieldsValues, setAdditionalFieldsValue] = useReducer((oldState, newState) => ({ ...oldState, ...newState }), {})
    const [gender, setGender] = useState('Male')
    const [birthDate, setBirthDate] = useState(null)
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [showManualPassword, setShowManualPassword] = useState(false)
    const [showLoginQRCode, setShowLoginQRCode] = useState(false)
    const [newPasswordRequired, setNewPasswordRequired] = useState(false)
    const timeout = useRef(null)
    const [emailExists, setEmailExists] = useState(false)
    const [emailValidated, setEmailValidated] = useState(false)
    const [emailValidText, setEmailValidText] = useState('')
    const [transferClassModalOpen, setTransferClassModalOpen] = useState(false)
    const [deleteModalOpen, setDelModalOpen] = useState(false)
    const [confirmDeleteStudentModal, setConfirmDeleteStudentModal] = useState(false)
    const [loading, setLoading] = useState(false)
    const [userQrCodeData, setUserQrCodeData] = useState({})

    const handleChange = (newValue) => setBirthDate(newValue)

    useEffect(() => {
        if (studentInfoOpen) {
            ReactModal.setAppElement('#root')
            const managementElement = document.getElementById('managementId')
            const sideBarElement = document.getElementById('sideBar')
            const studentAnalyticsElement = document.getElementById('studentAnalyticsId')
            document.getElementById('home').style.filter = 'blur(5px)'
            if (managementElement) {
                managementElement.style.filter = 'blur(5px)'
            }
            if (studentAnalyticsElement) {
                studentAnalyticsElement.style.filter = 'blur(5px)'
            }
            if (sideBarElement) {
                sideBarElement.style.filter = 'blur(5px)'
            }
        } else {
            document.getElementById('home').style.filter = 'unset'
            setUserQrCodeData({})
            setShowLoginQRCode(false)
            const managementElement = document.getElementById('managementId')
            const sideBarElement = document.getElementById('sideBar')
            const studentAnalyticsElement = document.getElementById('studentAnalyticsId')
            if (managementElement) {
                managementElement.style.filter = 'unset'
            }
            if (studentAnalyticsElement) {
                studentAnalyticsElement.style.filter = 'unset'
            }
            if (sideBarElement) {
                sideBarElement.style.filter = 'unset'
            }
        }
    }, [studentInfoOpen])

    useEffect(() => {
        if (student != {} && studentInfoOpen) {
            setValue('student_upn', student?.student_upn)
            setValue('first_name', student?.first_name)
            setValue('last_name', student?.last_name)
            setValue('email', student?.email)
            setGender(student?.gender)
            setBirthDate(student?.date_of_birth ? new Date(student.date_of_birth < 2000000000 ? student.date_of_birth * 1000 : student.date_of_birth) : null)
            setNewPasswordRequired(false)
            setShowManualPassword(false)
            setEmailValidated(!!student?.email)
            let additionalValues = {}
            additionalFields.forEach((element) => {
                additionalValues[element.code] = student[element.code] ?? ''
            })
            setAdditionalFieldsValue(additionalValues)
        }
    }, [student, studentInfoOpen])

    const enableSetupManuallyPassword = () => {
        setShowManualPassword(!showManualPassword)
    }

    const save = () => {
        // if (!emailValidated) return;
        if (emailExists || !emailValidated) {
            notify(emailValidText ?? 'Email is not valid')
            return
        }
        const formData = getValues()
        const studentPayload = { ...student, ...additionalFieldsValues }
        if (showManualPassword) {
            if (formData.password === formData.repeat_password) {
                studentPayload.password = formData.password
                studentPayload.new_password = true
                if (newPasswordRequired) studentPayload.new_password_required = true
                else delete studentPayload?.new_password_required
            } else {
                setError('password', { type: 'custom', message: "Passwords don't match" })
                return
            }
        }
        studentPayload.student_upn = formData.student_upn
        studentPayload.first_name = formData.first_name
        studentPayload.last_name = formData.last_name
        studentPayload.email = formData.email.toLowerCase()
        studentPayload.gender = gender?.toLocaleLowerCase()
        if (!!birthDate) studentPayload.date_of_birth = parseInt(birthDate.getTime())

        if (!!studentPayload.id) {
            setNewStudent(studentPayload)
            return
        }
        studentPayload.institution_id = user.institution_id
        setNewStudent(studentPayload)
    }

    const setNewStudent = (newStudent) => {
        if (currentClass.name == '' || currentClass.year == '') notify(t('Can`t create student without create class'))
        // newStudent.class_id = currentClass.id;
        newStudent.classesName = currentClass.name
        newStudent.classes = [
            {
                id: currentClass.id,
                name: currentClass.name,
                year: currentClass.year,
            },
        ]
        newStudent.year = currentClass.year
        newStudent.role = 0
        newStudent.validEmail = !dontValidateEmail
        updateStudentFunction(newStudent)
    }

    const updateStudentFunction = async (stud) => {
        setLoading(true)
        const resp = await updateStudent(stud)
        if (resp.status === 200) {
            currentClass.students?.forEach((element, index) => {
                if (element.id === stud.id) {
                    currentClass.students[index] = stud
                }
            })
            reloadStudentsList()
            closeStudentInfo()
            notify(t('Data is saved'))
            setLoading(false)
        } else {
            notify(t('yourDataIsNotUpdated'))
            setLoading(false)
        }
    }

    const validateEmail = async (email) => {
        if (student.email === email.toLowerCase().trim()) {
            setEmailValidText('')
            setEmailExists(false)
            setEmailValidated(true)
            return
        }
        const valid = validEmail(email)
        if (!valid) {
            setEmailValidText(t('emailIsInvalid'))
            setEmailExists(true)
        } else {
            const resp = await email_exists(email.toLowerCase())
            if (resp.status === 200) {
                const email_exists = resp.data
                setEmailValidText(email_exists ? t('emailAlreadyExists') : t('emailIsAvailable'))
                setEmailExists(email_exists)
                setEmailValidated(true)
            }
        }
    }

    function validEmail(e) {
        if (dontValidateEmail) return true
        var filter = /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
        return String(e).search(filter) !== -1
    }
    const handleChangeStudentEmail = (e) => {
        setEmailValidated(false)
        if (timeout.current !== null) {
            clearTimeout(timeout.current)
        }
        timeout.current = setTimeout(() => {
            validateEmail(e.target.value)
        }, 800)
    }

    const closeTransferClassModal = () => {
        setTransferClassModalOpen(false)
    }

    const closeDelModal = () => {
        setDelModalOpen(false)
    }

    const generatePassword = async () => {
        const resp = await generate_new_password(student)
        if (resp.status === 200) {
            student.emailSent = resp.data?.emailSent
            notify(t('New password is generated'))
            closeDelModal()
        } else {
            notify('Error')
            closeDelModal()
        }
    }

    const closeConfirmDeleteStudentModal = () => {
        setConfirmDeleteStudentModal(false)
    }

    const openConfirmDeleteStudentModal = () => {
        setConfirmDeleteStudentModal(true)
    }

    const removeStudent = async () => {
        if (student !== null || student !== undefined) {
            const resp = await deleteStudentAsync(student)
            if (resp.status === 200) {
                notify(t('studentRemoved'))
                closeConfirmDeleteStudentModal()
                closeStudentInfo()
                reloadStudentsList()
            } else {
            }
        } else {
        }
    }

    const closeInfoModal = () => {
        setTransferClassModalOpen(false)
        closeStudentInfo()
    }

    function isNumber(value) {
        return typeof value === 'number'
    }

    const isDisabled = useMemo(() => !emailValidated || loading, [emailValidated, loading])

    const resetModalValues = () => {
        reset()
        setEmailValidText('')
        setEmailValidated(false)
        setEmailExists(false)
        closeTransferClassModal()
        setLoading(false)
        setShowManualPassword(false)
        setBirthDate(null)
        setNewPasswordRequired(false)
    }

    const dontValidateEmail =
        user.institution_id === '62a6c9134d2524bd8331a463' ||
        user.institution_id === '6667f324764e8e3618263992' ||
        (user.institution_id === '5f25b9fc7fc9788dacc0ce7d' && ((env === 'staging' && parseInt(currentClass?.year) <= 5) || env !== 'staging')) || //Demo school
        (user.institution_id === '625486a1b964d3df1b2b9163' && parseInt(currentClass?.year) <= 5)

    const allowedQRCodeLogins = isQRCodeLoginEnabled(institution, user)

    const openQRCodeData = async () => {
        setShowLoginQRCode(true)
        setLoadingQrCode(true)
        const resp = await generateLoginQRCodeForStudent(student.id)
        if (resp.status === 200) {
            setUserQrCodeData(resp.data)
            setLoadingQrCode(false)
        }
    }

    const qrCodeData = `upstriveeducation://open?data=${encodeURIComponent(JSON.stringify(userQrCodeData))}`
    return (
        <ReactModal
            transparent
            closeTimeoutMS={600}
            isOpen={studentInfoOpen}
            onAfterOpen={() => {}}
            onAfterClose={() => resetModalValues()}
            onRequestClose={closeStudentInfo}
            className="noScroll"
            style={{
                overlay: {
                    zIndex: 100,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    backdropFilter: 'blur(5px)',
                },
                content: {
                    padding: 0,
                    opacity: 1,
                    width: 654,
                    margin: 'auto',
                    backgroundColor: 'transparent',
                    maxHeight: '100%',
                    border: 'none',
                    boxShadow: 'none',
                    overflow: 'scroll',
                    paddingTop: 35,
                    paddingBottom: 35,
                },
            }}
        >
            <div className="student-info-modal">
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div className="close-modal-icon " onClick={() => closeStudentInfo()}>
                        <CloseModalIcon />
                    </div>
                </div>

                <div className="student-info-title">
                    <p>{t('studentInfo')} </p>
                </div>
                <div className="w-full flex flex-row items-center justify-between mt-2">
                    <p className="text-deepPurple text-[18px] leading-[23px] font-medium ">
                        {t('Class')}: <span className="text-darkenPurple">{currentClass?.name}</span>
                    </p>

                    {!!student.id && (
                        <button
                            className="flex items-center justify-center px-8 rounded-full h-[50px] bg-lightBG"
                            onClick={() => setTransferClassModalOpen(true)}
                            id="transfer-to-class-student-button"
                        >
                            <p className="text-deepPurple text-[18px] leading-[22px] font-semibold ">{t('Transfer to another class')}</p>
                        </button>
                    )}
                </div>
                <div className="light-small-scroll mt-8" id="student-info-modal-scroll">
                    <form onSubmit={handleSubmit(save)}>
                        <div className={`student-info-form`}>
                            <div className={`student-input ${errors.student_upn ? 'pink-border' : ''}`} style={{ position: 'relative' }}>
                                <label style={{ color: errors.student_upn ? '#FF747F' : '' }}>{t('student')} UPN</label>
                                <input type="text" id="student-upn-input" placeholder={t('enterStudentUPN')} autoComplete="off" {...register('student_upn', { required: false })} />
                                {errors.student_upn && <span className="field-validation-text">{t('fieldIsRequired')}</span>}
                            </div>
                            <div className={`student-input ${errors.first_name ? 'pink-border' : ''}`} style={{ position: 'relative' }}>
                                <label style={{ color: errors.first_name ? '#FF747F' : '' }}>{t('firstName')} </label>
                                <input type="text" id="first-name-input" placeholder={t('enterName')} autoComplete="off" {...register('first_name', { required: true })} />
                                {errors.first_name && <span className="field-validation-text">{t('fieldIsRequired')}</span>}
                            </div>
                            <div className={`student-input ${errors.last_name ? 'pink-border' : ''}`} style={{ position: 'relative' }}>
                                <label style={{ color: errors.last_name ? '#FF747F' : '' }}>{t('lastName')} </label>
                                <input type="text" id="last-name-input" placeholder={t('enterSurname')} autoComplete="off" {...register('last_name', { required: true })} />
                                {errors.last_name && <span className="field-validation-text">{t('fieldIsRequired')}</span>}
                            </div>
                            <div className="student-input " style={{ position: 'relative' }}>
                                <div
                                    className="student-info-select-button"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        setIsDatePickerOpen(true)
                                    }}
                                    style={{ marginTop: 0, zIndex: 3 }}
                                >
                                    <p>{t('dateOfBirth')} </p>
                                    <span style={{ flex: 1 }}></span>
                                    <p>{!!birthDate ? formatDateOnly(birthDate) : '-'}</p>
                                </div>
                                <div style={{ position: 'absolute', top: 0, zIndex: 2, visibility: 'hidden' }}>
                                    <LocalizationProvider adapterLocale={localeMap[i18n.language]} dateAdapter={AdapterDateFns}>
                                        <DesktopDatePicker
                                            open={isDatePickerOpen}
                                            onClose={() => setIsDatePickerOpen(false)}
                                            label="Date desktop"
                                            inputFormat="MM/dd/yyyy"
                                            value={birthDate}
                                            onChange={handleChange}
                                            maxDate={new Date()}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </div>

                            <SelectInput name={t('gender')} array={['male', 'female']} value={t(gender?.toLowerCase())} callbackClick={setGender} translate />

                            {additionalFields.map((field) => {
                                return (
                                    <SelectInput
                                        name={field?.title}
                                        array={field?.options ?? []}
                                        key={field?.code}
                                        value={additionalFieldsValues[field?.code]}
                                        callbackClick={(value) => {
                                            let data = {}
                                            data[field?.code] = value
                                            setAdditionalFieldsValue(data)
                                        }}
                                    />
                                )
                            })}

                            <div className={`student-input ${errors.email ? 'pink-border' : ''}`} style={{ marginTop: 30, marginBottom: 0, position: 'relative' }}>
                                <label style={{ color: errors.email ? '#FF747F' : '' }}>{t('email')}</label>
                                <input
                                    type="text"
                                    id="student-email-input"
                                    placeholder={t('enterEmail')}
                                    autoComplete="off"
                                    {...register('email', {
                                        required: true,
                                        // pattern: null,
                                        pattern: dontValidateEmail ? false : /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                                    })}
                                    onChange={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        handleChangeStudentEmail(e)
                                        setValue('email', e.target.value, { shouldValidate: true })
                                    }}
                                />
                                <span className="field-validation-text">{errors.email ? t('emailIsInvalid') : emailValidText}</span>
                            </div>
                            {/* {getValues()['email']} */}
                        </div>
                        {isNumber(student?.emailSent) && (
                            <div className="w-full mt-[15px] bg-lightBG rounded-[15px] h-[80px] flex justify-center gap-[51px] items-center ">
                                <p className=" text-deepPurple text-lg font-semibold -tracking-[0.36px] ">{t('Last email with password was sent')}: </p>
                                <p className=" text-darkenPurple text-lg font-semibold -tracking-[0.36px] ">
                                    {format(student?.emailSent < 2000000000 ? student?.emailSent * 1000 : student?.emailSent, 'dd/MM/yyyy HH:mm')}
                                </p>
                            </div>
                        )}

                        <div className=" mt-[50px] mr-[5px]">
                            {showLoginQRCode && (
                                <div className="student-info-form mb-[15px]">
                                    <div className="flex justify-center mb-[20px]">
                                        {!loadingQrCode && (
                                            <QRCodeCanvas
                                                value={JSON.stringify(qrCodeData)}
                                                size={256} // Size in px
                                                bgColor="#ffffff" // Background color
                                                fgColor="#413f5e" // Foreground (QR) color
                                                level="L" // Error correction level (L, M, Q, H)
                                                marginSize={0}
                                            />
                                        )}

                                        {loadingQrCode && (
                                            <div className="flex flex-col items-center justify-center animate-pulse">
                                                <div className="w-64 h-64 bg-upstrive-light rounded-lg  flex items-center justify-center">
                                                    <Loading primaryColor={'#5e639b'} secondaryColor={'#5e639b'} size={30} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div
                                        className="transparent-button "
                                        id="hide-login-qr-code-button"
                                        onClick={() => setShowLoginQRCode(false)}
                                        style={{ width: '100%', fontSize: 18, height: 65, padding: 22 }}
                                    >
                                        <p>{t('Hide login QR code')} </p>
                                    </div>
                                </div>
                            )}
                            {!!student?.id && !showLoginQRCode && allowedQRCodeLogins && (
                                <div
                                    className="transparent-button mb-[20px]"
                                    id="show-login-qr-code-button"
                                    onClick={openQRCodeData}
                                    style={{ width: '100%', fontSize: 18, height: 65, padding: 22 }}
                                >
                                    <p>{t('Generate login QR code')} </p>
                                </div>
                            )}
                            {showManualPassword && (
                                <div className="student-info-form mt-[15px]">
                                    <div className={`student-input ${errors.password ? 'pink-border' : ''}`} style={{ position: 'relative' }}>
                                        <label style={{ color: errors.password ? '#FF747F' : '' }}>{t('password')} </label>
                                        <input
                                            type="password"
                                            id="student-password-input"
                                            placeholder={t('password')}
                                            autoComplete="off"
                                            {...register('password', { required: true })}
                                        />
                                        {errors?.password?.type == 'required' && <span className="field-validation-text">{t('fieldIsRequired')}</span>}
                                        {!!errors?.password?.message && <span className="field-validation-text">{t('passwordsDoNotMatch')} </span>}
                                    </div>

                                    <div className={`student-input ${errors.repeat_password ? 'pink-border' : ''}`} style={{ position: 'relative' }}>
                                        <label style={{ color: errors.repeat_password ? '#FF747F' : '' }}>{t('repeatPassword')} </label>
                                        <input
                                            type="password"
                                            id="student-repeat-password-input"
                                            placeholder={t('password')}
                                            autoComplete="off"
                                            {...register('repeat_password', { required: true })}
                                        />

                                        {errors.repeat_password && <span className="field-validation-text">{t('fieldIsRequired')}</span>}
                                    </div>
                                    <div className="password-settings-switch">
                                        <label
                                            id="student-password-setting-switch-let-them-set-their-own-passwords-after-login"
                                            className={`password-setting-switch-item ${newPasswordRequired ? 'selected-password-item' : 'password-setting-switch-item-hover'}`}
                                            onClick={() => setNewPasswordRequired(true)}
                                        >
                                            {t('letThemSetTheirOwnPasswordsAfterLogin')}
                                        </label>
                                        <label
                                            id="student-password-setting-switch-keep-this-password"
                                            className={`password-setting-switch-item ${!newPasswordRequired ? 'selected-password-item' : 'password-setting-switch-item-hover'}`}
                                            onClick={() => setNewPasswordRequired(false)}
                                        >
                                            {t('keepThisPassword')}
                                        </label>
                                    </div>
                                    <div
                                        className="transparent-button "
                                        id="hide-password-manager-button"
                                        onClick={enableSetupManuallyPassword}
                                        style={{ width: '100%', fontSize: 18, height: 65, padding: 22 }}
                                    >
                                        <p>{t('hidePasswordmanager')} </p>
                                    </div>
                                </div>
                            )}
                            {!showManualPassword && (
                                <div
                                    className="transparent-button "
                                    id="set-students-password-manually-button"
                                    onClick={enableSetupManuallyPassword}
                                    style={{ width: '100%', fontSize: 18, height: 65, padding: 22 }}
                                >
                                    <p>{t('setStudentsPasswordManually')} </p>
                                </div>
                            )}
                            {!!student?.id && (
                                <div
                                    id="generate-new-password-button"
                                    className="transparent-button mt-[20px]"
                                    onClick={() => setDelModalOpen(true)}
                                    style={{ width: '100%', fontSize: 18, height: 65, padding: 22 }}
                                >
                                    <p>{t('generateNewPassword')} </p>
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'row', marginTop: !!student?.id ? 20 : 30 }}>
                            {!student?.id && (
                                <div
                                    className="transparent-button"
                                    id="close-popup-button"
                                    onClick={closeStudentInfo}
                                    style={{ fontSize: 18, margin: 'auto', width: 322, height: 65, padding: 22 }}
                                >
                                    <p>{t('closePopUp')} </p>
                                </div>
                            )}

                            {!!student?.id && (
                                <div
                                    id="delete-student-button"
                                    className="delete-student-button"
                                    onClick={() => {
                                        openConfirmDeleteStudentModal()
                                    }}
                                >
                                    <p>{t('deleteStudent')} </p>
                                </div>
                            )}
                            <button
                                id="student-save-button"
                                className="save-submit-button"
                                style={{
                                    margin: 'auto',
                                    width: !!student?.id ? 347 : 192,
                                    cursor: isDisabled ? 'default' : 'pointer',
                                }}
                                disabled={isDisabled}
                            >
                                {loading ? (
                                    <Loading primaryColor={isDisabled ? '#fddf6d' : '#5e639b'} secondaryColor={isDisabled ? '#fddf6d' : '#5e639b'} />
                                ) : (
                                    <div className="flex flex-row items-center justify-center gap-[10px]">
                                        <p style={{ marginRight: 10 }}>{t('save')} </p>
                                        <Save />
                                    </div>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
                {studentInfoOpen && (
                    <TransferToAnotherClassModal
                        transferClassModalOpen={transferClassModalOpen}
                        closeTransferClassModal={closeTransferClassModal}
                        student={student}
                        currentClass={currentClass}
                        updateClassStudents={updateClassStudents}
                        closeInfoModal={closeInfoModal}
                    />
                )}
                {studentInfoOpen && (
                    <ConfirmationModal
                        modalIsOpen={deleteModalOpen}
                        closeModal={closeDelModal}
                        confirmation={generatePassword}
                        message={
                            isNumber(student?.emailSent) && isToday(student?.emailSent)
                                ? t('New password has already been generated today for this user! Do you want to generate it again?')
                                : t('You are going to generate new password for this student?')
                        }
                    />
                )}
                {studentInfoOpen && (
                    <ConfirmationModal
                        modalIsOpen={confirmDeleteStudentModal}
                        closeModal={closeConfirmDeleteStudentModal}
                        confirmation={removeStudent}
                        message={t('doYouWantToDeleteThis')}
                    />
                )}
            </div>
        </ReactModal>
    )
}

export default StudentInfoModal
